import { Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PersonalInformationService } from '@services/api/internal/personal-information.service';
import { LegalOrganizationManager } from '@shared/interfaces/profile/legal-organization-manager';
import { ManagerInfo } from '@shared/interfaces/users/manager-info';
import { DefaultWarehouse } from '@shared/interfaces/users/default-warehouse';
import { createWarehouseFallbackManager, isManagerContactEmpty } from '@shared/utils/manager-warehouse-fallback';
import { catchError, combineLatest, distinctUntilChanged, map, of, switchMap } from 'rxjs';

export function createOrderingBlockedManagerSignal(
  active: Signal<boolean>,
  legalOrganizationId: Signal<string | null>,
  personalInformationService: PersonalInformationService,
  fallbackManager: Signal<ManagerInfo | null>,
  fallbackWarehouse: Signal<DefaultWarehouse | null>,
): Signal<LegalOrganizationManager | null> {
  return toSignal(
    combineLatest([
      toObservable(active),
      toObservable(legalOrganizationId),
      toObservable(fallbackManager),
      toObservable(fallbackWarehouse),
    ]).pipe(
      distinctUntilChanged(
        (
          [prevActive, prevLegalOrganizationId, prevFallbackManager, prevFallbackWarehouse],
          [currentActive, currentLegalOrganizationId, currentFallbackManager, currentFallbackWarehouse],
        ) =>
          prevActive === currentActive &&
          prevLegalOrganizationId === currentLegalOrganizationId &&
          prevFallbackManager === currentFallbackManager &&
          prevFallbackWarehouse === currentFallbackWarehouse,
      ),
      switchMap(([isActive, currentLegalOrganizationId, currentFallbackManager, currentFallbackWarehouse]) => {
        if (!isActive) {
          return of(null);
        }

        // Priority chain, the first non-empty source is taken as a whole:
        // 1) the manager from /me wins outright and spares the extra request.
        const normalizedFallbackManager = normalizeFallbackManager(currentFallbackManager);
        if (!isManagerContactEmpty(normalizedFallbackManager)) {
          return of(normalizedFallbackManager);
        }

        // 3) the default warehouse contact is the last usable source before giving up.
        const warehouseContact = createWarehouseFallbackManager(currentFallbackWarehouse);

        // 2) otherwise load the legal organization manager, when there is one to load.
        if (!currentLegalOrganizationId) {
          return of(warehouseContact);
        }

        return personalInformationService.getPersonalInformation(currentLegalOrganizationId).pipe(
          map(({ legalOrganizationManager }) => {
            const organizationManager = legalOrganizationManager ?? null;

            return isManagerContactEmpty(organizationManager) ? warehouseContact : organizationManager;
          }),
          // A failed request is treated the same as an empty result: fall further down the chain.
          catchError(() => of(warehouseContact)),
        );
      }),
    ),
    { initialValue: null },
  );
}

function normalizeFallbackManager(manager: ManagerInfo | null): LegalOrganizationManager | null {
  if (!manager) {
    return null;
  }

  return {
    id: manager.id,
    name: manager.name,
    phoneNumber: null,
    phones: manager.phones,
    emails: manager.emails,
  };
}
